'use client';

import {
  type GooglePlaceDto,
  LocationsPublicApi,
} from '@up2stay/up2promo-api-client';
import { Input } from '@up2stay/up2promo-ui';
import { Skeleton } from '@up2stay/up2promo-ui';
import {
  Command,
  CommandEmpty,
  CommandItem,
  CommandList,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@up2stay/up2promo-ui';
import { Loader2, MapPin } from 'lucide-react';
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  keyboardAwarePopoverCollision,
  useCssKeyboardInset,
} from '@/core/hooks/use-css-keyboard-inset';
import { useDictionary } from '@/core/providers/dictionary-provider';

const DEBOUNCE_MS = 400;
const MIN_SEARCH_CHARS = 3;

export interface LocationSearchComboboxProps {
  onSelect: (place: GooglePlaceDto) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  establishmentPoiOnly?: boolean;
  /** Input id for a visible `<Label htmlFor>` (DEV-540). */
  id?: string;
  /** Accessible name when there is no visible label. */
  'aria-label'?: string;
}

export function LocationSearchCombobox({
  onSelect,
  placeholder = 'Search for a place...',
  disabled = false,
  className,
  establishmentPoiOnly = false,
  id,
  'aria-label': ariaLabel,
}: LocationSearchComboboxProps) {
  const dict = useDictionary();
  const searchStepDict = (
    dict as {
      locations?: {
        create?: {
          wizard?: {
            searchStep?: {
              typeToSearch?: string;
              minChars?: string;
              searching?: string;
              noPlacesFound?: string;
              suggestionsLabel?: string;
            };
          };
        };
      };
    }
  ).locations?.create?.wizard?.searchStep;
  const emptyHint = searchStepDict?.typeToSearch;
  const minCharsHint = searchStepDict?.minChars;
  const searchingLabel = searchStepDict?.searching;
  const noPlacesFoundLabel = searchStepDict?.noPlacesFound;
  const suggestionsLabel = searchStepDict?.suggestionsLabel;

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<GooglePlaceDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Last input whose search finished; differs while the debounce is pending. */
  const [settledInput, setSettledInput] = useState('');
  const [resolvingPlaceId, setResolvingPlaceId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  /** Highlighted suggestion (cmdk value = place id); arrows move it, Enter picks it. */
  const [activeValue, setActiveValue] = useState('');
  /** Set by a keyboard pick: refocus once a parent re-enables the field. */
  const refocusAfterPickRef = useRef(false);
  const keyboardInset = useCssKeyboardInset(open);

  const searchInput = inputValue.trim();
  const searchTooShort = !searchInput || searchInput.length < MIN_SEARCH_CHARS;

  const [prevSearchInput, setPrevSearchInput] = useState(searchInput);
  if (searchInput !== prevSearchInput) {
    setPrevSearchInput(searchInput);
    if (searchTooShort) {
      setSuggestions([]);
      setError(null);
      setIsLoading(false);
    }
  }

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!input) {
        setSuggestions([]);
        setError(null);
        return;
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();
      setIsLoading(true);
      setError(null);
      try {
        // Predictions only: Place Details are billed per call, so they're
        // loaded once for the place the user picks (handleSelect).
        const response = await LocationsPublicApi.searchGooglePlaces({
          query: {
            input,
            includeDetails: false,
            ...(establishmentPoiOnly ? { establishmentPoiOnly: true } : {}),
          },
        });
        const data = response.data ?? [];
        setSuggestions(Array.isArray(data) ? data : []);
        setSettledInput(input);
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') {
          return;
        }
        const message =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ??
          (err instanceof Error ? err.message : 'Search failed');
        setError(message);
        setSuggestions([]);
        setSettledInput(input);
      } finally {
        setIsLoading(false);
        abortRef.current = null;
      }
    },
    [establishmentPoiOnly],
  );

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (searchTooShort) {
      return;
    }
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void fetchSuggestions(searchInput);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchInput, searchTooShort, fetchSuggestions]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const handleSelect = useCallback(
    async (place: GooglePlaceDto) => {
      if (resolvingPlaceId) return;
      setResolvingPlaceId(place.id);
      let resolved = place;
      try {
        const response = await LocationsPublicApi.getGooglePlaceById({
          path: { placeId: place.id },
        });
        const details = response.data;
        if (details) {
          resolved = {
            ...place,
            ...details,
            name: details.name || place.name,
            address: details.address || place.address,
          };
        }
      } catch {
        // Details lookup failed: the prediction (id, name, address) still works.
      } finally {
        setResolvingPlaceId(null);
      }
      onSelect(resolved);
      setOpen(false);
      setInputValue('');
      setSuggestions([]);
    },
    [onSelect, resolvingPlaceId],
  );

  const showList =
    open &&
    (searchInput.length >= MIN_SEARCH_CHARS ||
      suggestions.length > 0 ||
      isLoading);
  const selectableSuggestions = showList && !error ? suggestions : [];
  const activeId = selectableSuggestions.some(
    (place) => place.id === activeValue,
  )
    ? activeValue
    : (selectableSuggestions[0]?.id ?? '');

  // The input sits outside cmdk's root, so it drives the highlight itself
  // (DEV-547). Enter is claimed here so the shared Input's "Enter blurs"
  // behaviour does not drop focus without picking anything.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const count = selectableSuggestions.length;
      if (count === 0) return;
      const index = selectableSuggestions.findIndex(
        (place) => place.id === activeId,
      );
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = selectableSuggestions[(index + step + count) % count];
      if (next) setActiveValue(next.id);
      return;
    }
    if (event.key === 'Enter') {
      const place = selectableSuggestions.find((item) => item.id === activeId);
      if (place) {
        event.preventDefault();
        refocusAfterPickRef.current = true;
        handleSelect(place);
      }
    }
  };

  // Parents may disable the field while they process the pick (competitors),
  // which drops focus to <body>; give it back so keyboard users can go on.
  useEffect(() => {
    if (disabled || !refocusAfterPickRef.current) return;
    refocusAfterPickRef.current = false;
    if (document.activeElement === document.body) inputRef.current?.focus();
  }, [disabled]);

  // Points the combobox at the list and its highlighted option for screen
  // readers. cmdk owns the option ids, so they are read from the DOM.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const list = listRef.current;
    const option =
      open && list && activeId
        ? list.querySelector<HTMLElement>(
            `[cmdk-item][data-value="${CSS.escape(activeId)}"]`,
          )
        : null;
    if (list?.id && open) input.setAttribute('aria-controls', list.id);
    else input.removeAttribute('aria-controls');
    if (option?.id) {
      input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }, [open, activeId, suggestions]);

  // Debounce pending: show "searching" instead of a premature "no places".
  const awaitingResults =
    isLoading || (!searchTooShort && searchInput !== settledInput);

  const emptyContent = (
    <div className="flex flex-col items-center justify-center gap-1 py-4 text-center text-sm">
      {awaitingResults ? (
        <>
          <Skeleton className="h-4 w-24 rounded" />
          <span className="text-muted-foreground">{searchingLabel}</span>
        </>
      ) : error ? (
        <span className="text-destructive">{error}</span>
      ) : searchInput.length > 0 && searchInput.length < MIN_SEARCH_CHARS ? (
        <span className="text-muted-foreground">{minCharsHint}</span>
      ) : searchInput ? (
        <span className="text-muted-foreground">{noPlacesFoundLabel}</span>
      ) : (
        <span className="text-muted-foreground">{emptyHint}</span>
      )}
    </div>
  );

  const handleInteractOutside = useCallback((e: Event) => {
    const target = e.target as Node | null;
    if (anchorRef.current?.contains(target)) {
      e.preventDefault();
    }
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className={className}>
          <Input
            ref={inputRef}
            id={id}
            aria-label={ariaLabel}
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              // Typing after Escape shows the results again.
              setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setOpen(true)}
            disabled={disabled}
            className="shadow-sm"
            aria-expanded={open}
            aria-autocomplete="list"
            role="combobox"
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] max-h-[var(--radix-popover-content-available-height)] min-w-[280px] overflow-hidden p-0"
        align="start"
        side="bottom"
        sideOffset={4}
        sticky="always"
        collisionPadding={keyboardAwarePopoverCollision(keyboardInset)}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={handleInteractOutside}
      >
        <Command
          shouldFilter={false}
          value={activeId}
          onValueChange={setActiveValue}
          className="flex max-h-[inherit] flex-col overflow-hidden rounded-lg border-0 shadow-none"
        >
          <CommandList
            ref={listRef}
            label={suggestionsLabel}
            className="max-h-[min(300px,calc(70dvh-var(--keyboard-inset-height,0px)))]"
          >
            {!showList ? (
              <CommandEmpty>{emptyContent}</CommandEmpty>
            ) : isLoading && suggestions.length === 0 ? (
              <CommandEmpty>{emptyContent}</CommandEmpty>
            ) : error ? (
              <CommandEmpty>{emptyContent}</CommandEmpty>
            ) : suggestions.length === 0 &&
              searchInput.length >= MIN_SEARCH_CHARS ? (
              <CommandEmpty>{emptyContent}</CommandEmpty>
            ) : suggestions.length === 0 ? (
              <CommandEmpty>{emptyContent}</CommandEmpty>
            ) : (
              suggestions.map((place) => (
                <CommandItem
                  key={place.id}
                  value={place.id}
                  onSelect={() => void handleSelect(place)}
                  disabled={resolvingPlaceId != null}
                  className="cursor-pointer gap-2"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate font-medium">{place.name}</span>
                    {place.address ? (
                      <span className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                        <MapPin className="size-3 shrink-0" />
                        {place.address}
                      </span>
                    ) : null}
                  </div>
                  {resolvingPlaceId === place.id ? (
                    <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
                  ) : null}
                </CommandItem>
              ))
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
